import { useAuth } from 'hooks/AuthContext'
import { getPathFrom, removePahtFrom } from 'hooks/usePathsRedirect'
import { usePrepareCart } from 'hooks/usePrepareCart'
import { setUser } from 'hooks/useSessionUser'
import { User } from 'hooks/useSignedUser/types'
import { useTokenApi } from 'hooks/useTokenApi'
import { RegistrationContextValue } from 'pages/RegistrationAssociation/hooks/useRegistration'
import { FormEvent, useState } from 'react'
import { useHistory } from 'react-router'
import { toast } from 'react-toastify'
import api, { defaultsHeadersAxios } from 'services/api'
import { clearSessionCartId, getSessionCartId, markSessionCartResolved } from 'utils/anonymousCart'
import { clearMask } from 'utils/formatters'
import { z } from 'zod'
import { getFieldsWithErrorsMessage } from '../helpers'

export interface RegisterSuccess {
  success: boolean
  token: string
  user: User
  order_id?: number
  skipped_items?: { product_id: number; name: string }[]
}

export interface RegisterError {
  error: string
  success: boolean
}

// Schema de validação para cadastro de associação
const associationRegistrationSchema = z
  .object({
    name: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
    email: z.string().email('E-mail inválido'),
    cpf: z.string().min(14, 'CPF inválido'),
    birthdate: z.string().min(10, 'Data de nascimento inválida'),
    password: z
      .string()
      .min(8, 'Senha deve ter pelo menos 8 caracteres')
      .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
      .regex(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula')
      .regex(/[0-9]/, 'Senha deve conter pelo menos um número')
      .regex(/[^A-Za-z0-9]/, 'Senha deve conter pelo menos um caractere especial'),
    passwordConfirmation: z.string(),
    genderIdentity: z.string().min(1, 'Identidade de gênero é obrigatória'),
    gender: z.string().min(1, 'Gênero é obrigatório'),
    nationality: z.string().min(1, 'Nacionalidade é obrigatória'),
    rg: z.string().min(1, 'RG é obrigatório'),
    issuingAuthorityRg: z.string().min(1, 'Órgão emissor é obrigatório'),
    issuingAuthorityUfRg: z.string().min(2, 'UF do órgão emissor é obrigatória'),
    cellphone: z.string().min(14, 'Telefone inválido'),
    addressCep: z.string().min(9, 'CEP inválido'),
    address: z.string().min(1, 'Endereço é obrigatório'),
    addressNumber: z.string().min(1, 'Número é obrigatório'),
    addressComplement: z.string().max(64, 'Complemento deve ter no máximo 64 caracteres'),
    addressDistrict: z.string().min(1, 'Bairro é obrigatório'),
    addressCity: z.string().min(1, 'Cidade é obrigatória'),
    addressState: z.string().min(2, 'Estado é obrigatório'),
    emailConfirmation: z.string().email('E-mail inválido')
  })
  .refine((data) => data.email === data.emailConfirmation, {
    message: 'Os e-mails não coincidem',
    path: ['emailConfirmation']
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'As senhas não coincidem',
    path: ['passwordConfirmation']
  })

const useRegistrationAssociationForm = (formFields: RegistrationContextValue) => {
  const history = useHistory()
  const { setTokenApi } = useTokenApi()
  const { getRoles } = useAuth()
  const { setOrderId } = usePrepareCart()

  const {
    name,
    email,
    emailConfirmation,
    cpf,
    birthdate,
    password,
    passwordConfirmation,
    genderIdentity,
    gender,
    nationality,
    rg,
    issuingAuthorityRg,
    issuingAuthorityUfRg,
    cellphone,
    telephone,
    addressCep,
    address,
    addressNumber,
    addressComplement,
    addressDistrict,
    addressCity,
    addressState,
    healthPlan,
    uploadedDocuments, // Documentos já com URLs do S3

    errors: _errors,
    setErrors,
    setLoading,
    removeFieldFromErrors: _removeFieldFromErrors
  } = formFields

  const [receivePromotionalEmails, setReceivePromotionalEmails] = useState(false)

  const onRegistrationFormSubmit = async (event: FormEvent) => {
    event.preventDefault()

    const validationData = {
      name,
      email,
      emailConfirmation,
      cpf,
      birthdate,
      password,
      passwordConfirmation,
      genderIdentity,
      gender,
      nationality,
      rg,
      issuingAuthorityRg,
      issuingAuthorityUfRg,
      cellphone,
      addressCep,
      address,
      addressNumber,
      addressComplement,
      addressDistrict,
      addressCity,
      addressState
    }

    const requiredFieldsValidation = await associationRegistrationSchema.safeParseAsync(validationData)

    if (!requiredFieldsValidation.success) {
      const newErrors = requiredFieldsValidation.error.errors
      setErrors(newErrors)
      toast.dismiss()
      toast.error(getFieldsWithErrorsMessage(newErrors), {
        autoClose: false
      })
      return
    }
    setErrors(undefined)

    try {
      setLoading(true)

      const sessionCartId = getSessionCartId()
      const {
        data: { token, user, order_id }
      } = await api.post<RegisterSuccess>('/register_association', {
        name,
        email,
        cpf: clearMask(cpf),
        birthday: birthdate,
        association_name: 'ASPAEC', // Nome fixo da associação
        password,
        gender_identity: genderIdentity,
        gender,
        nationality,
        rg,
        issuing_authority_rg: issuingAuthorityRg,
        issuing_authority_uf_rg: issuingAuthorityUfRg,
        phone: clearMask(telephone) || clearMask(cellphone),
        whatsapp: clearMask(cellphone),
        newsletter: receivePromotionalEmails,
        address,
        address_number: addressNumber,
        address_complement: addressComplement || '',
        address_district: addressDistrict,
        address_city: addressCity,
        address_state: addressState,
        address_cep: clearMask(addressCep),
        health_plan: healthPlan || '',
        documents: uploadedDocuments, // Documentos já com URLs do S3 (upload feito no momento da seleção)
        ...(sessionCartId ? { session_cart_id: sessionCartId } : {})
      })

      if (sessionCartId) {
        markSessionCartResolved(sessionCartId)
      }

      setTokenApi(token)
      defaultsHeadersAxios(token)
      await setUser(user)
      getRoles()

      if (order_id) {
        setOrderId(order_id)
        clearSessionCartId()
        history.push('/carrinho')
      } else {
        const pathFrom = await getPathFrom()
        const pageToRedirect = pathFrom || '/AreaAssociados/MinhaConta'
        history.push(pageToRedirect)
      }

      await removePahtFrom()

      toast.success('Cadastro realizado com sucesso!')
    } catch (err: any) {
      const error = err?.response?.data as RegisterError
      toast.error(error?.error ? error?.error : 'Houve um erro ao realizar o cadastro.')
    } finally {
      setLoading(false)
    }
  }

  return {
    onRegistrationFormSubmit,
    receivePromotionalEmails,
    setReceivePromotionalEmails
  }
}

export default useRegistrationAssociationForm
